import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['digit', 'phone', 'email', 'resend', 'label', 'form']

  static values = { wait: Number, resendUrl: String }

  connect() {
    this.remaining = this.waitValue || 0
    this.renderWait()
    if (this.remaining > 0) this.arm()
    if (this.visible()) this.digitTargets[0]?.focus()
  }

  disconnect() {
    window.clearInterval(this.timer)
  }

  start(seconds, { focus = false } = {}) {
    window.clearInterval(this.timer)
    this.remaining = Number(seconds) || 0
    this.renderWait()
    if (this.remaining > 0) this.arm()
    if (focus) this.digitTargets[0]?.focus()
  }

  onInput(event) {
    const digits = event.target.value.replace(/\D/g, '')
    event.target.value = ''
    if (digits) this.place(event.target, digits)
  }

  onPaste(event) {
    const digits = (event.clipboardData?.getData('text') || '').replace(/\D/g, '')
    if (!digits) return

    event.preventDefault()
    this.place(event.target, digits)
  }

  onKeydown(event) {
    const inputs = this.digitTargets
    const index = inputs.indexOf(event.target)
    if (event.key === 'Backspace' && event.target.value === '' && index > 0) {
      event.preventDefault()
      inputs[index - 1].value = ''
      inputs[index - 1].focus()
      return
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      inputs[index - 1].focus()
    }

    if (event.key === 'ArrowRight' && index < inputs.length - 1) {
      event.preventDefault()
      inputs[index + 1].focus()
    }
  }

  submit(event) {
    if (!this.complete()) {
      event.preventDefault()
      this.digitTargets.find((input) => input.value === '')?.focus()
      return
    }

    if (this.submitting) {
      event.preventDefault()
      return
    }

    event.preventDefault()
    this.submitting = true
    this.postCode().finally(() => {
      this.submitting = false
    })
  }

  async resend(event) {
    event.preventDefault()
    if (!this.hasResendTarget || this.resendTarget.disabled || this.resending) return

    this.resending = true
    try {
      const response = await fetch(this.resendUrlValue, {
        method: 'POST',
        headers: this.jsonHeaders(),
      })
      const payload = await response.json().catch(() => ({}))
      this.start(payload.resend_in)
    } finally {
      this.resending = false
    }
  }

  clear() {
    this.digitTargets.forEach((input) => {
      input.value = ''
    })
    this.digitTargets[0]?.focus()
  }

  place(start, digits) {
    const inputs = this.digitTargets
    if (digits.length === 1) {
      start.value = digits
      const next = inputs[inputs.indexOf(start) + 1]
      if (next) next.focus()
      else if (this.complete()) this.formTarget.requestSubmit()
      return
    }

    inputs.forEach((input, index) => {
      input.value = digits[index] || ''
    })
    inputs[Math.min(digits.length, inputs.length) - 1]?.focus()
    if (this.complete()) this.formTarget.requestSubmit()
  }

  async postCode() {
    const body = new FormData(this.formTarget)
    body.set('code', this.digitTargets.map((input) => input.value).join(''))
    try {
      const response = await fetch(this.formTarget.action, {
        method: 'POST',
        body,
        headers: this.jsonHeaders(),
        redirect: 'manual',
      })
      const payload = await response.json().catch(() => ({}))
      if (payload.redirect) {
        window.location.assign(payload.redirect)
        return
      }
    } catch {
      this.formTarget.submit()
      return
    }

    this.clear()
  }

  complete() {
    return this.digitTargets.length === 6 && this.digitTargets.every((input) => /^\d$/.test(input.value))
  }

  visible() {
    return !this.element.hidden && !this.element.closest('[hidden]')
  }

  arm() {
    this.timer = window.setInterval(() => {
      this.remaining -= 1
      this.renderWait()
      if (this.remaining <= 0) window.clearInterval(this.timer)
    }, 1000)
  }

  renderWait() {
    if (!this.hasResendTarget || !this.hasLabelTarget) return

    if (this.remaining <= 0) {
      this.resendTarget.disabled = false
      this.labelTarget.textContent = 'Send a new code'
      return
    }

    const minutes = Math.floor(this.remaining / 60)
    const seconds = String(this.remaining % 60).padStart(2, '0')
    this.resendTarget.disabled = true
    this.labelTarget.textContent = `Send a new code in ${minutes}:${seconds}`
  }

  jsonHeaders() {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    return {
      Accept: 'application/json',
      'X-CSRF-Token': token,
      'X-Account-Sign-In': '1',
    }
  }
}
